import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import ImageGallery from "react-image-gallery";
import { useMemo, useEffect } from "react";
import { themeStyles } from "../../common/styles";
import fb from "../../assets/social/facebook.svg";
import insta from "../../assets/social/instagram.svg";

import "./LocationLanding.scss";

// Location and gallery imports
import { LOCATIONS } from "../../config/locations";
import { getImagesForLocation } from "../../assets/gallery/galleryImages";
import { getLocationImages, preloadImage } from "../../utils/imageLoader";

const LocationLanding = () => {
  const navigate = useNavigate();
  const { location } = useParams();

  // Get images for the specific location
  // NOTE: Images are loaded once when component mounts and won't change when location changes
  // To prevent photos from changing when location changes, we use an empty dependency array []
  const images = useMemo(() => {
    // Try to get location-specific images first
    let images = getImagesForLocation(location);

    // If no images found, try the utility function as fallback
    if (!images || images.length === 0) {
      images = getLocationImages(location);
    }

    return images
      .filter(Boolean) // Ensures none of them are undefined
      .map((img) => ({
        original: img,
        thumbnail: img,
      }))
      .sort(() => Math.random() - 0.5); // Shuffle images for variety
  }, []); // Empty dependency array prevents re-rendering when location changes

  // Preload first gallery image for faster LCP
  useEffect(() => {
    const firstSrc = images[0]?.original;
    if (firstSrc) return preloadImage(firstSrc);
  }, [images]);

  const handleBooking = () => {
    // Save location to localStorage
    localStorage.setItem("selectedLocation", location);

    // Navigate to booking page
    navigate(`/book/${location.toLowerCase()}`);
  };

  const locationDisplayName = LOCATIONS[location]?.displayName || location;

  return (
    <>
      <div className="main-background">
        {/* Background Slider */}
        <div className="gallery-wrapper">
          <ImageGallery
            items={images}
            showFullscreenButton={false}
            showPlayButton={false}
            showThumbnails={false}
            lazyLoad={true}
            autoPlay={true}
            slideInterval={5000}
            slideDuration={800}
            showNav={false}
            showBullets={false}
            className="gallery-image"
          />
        </div>

        {/* Booking Section */}
        <div className="book-now-btn-wrapper booking-ui-wrapper">
          {/* <h1 className="booking-heading">BRING YOUR GAME ON!</h1> */}

          <div className="booking-controls">
            <Button
              className="book-now-btn"
              sx={{
                backgroundColor: themeStyles.secondaryColor,
                color: themeStyles.secondaryTextColor,
                textTransform: "unset",
                padding: "10px 24px",
                fontSize: "14px",
                fontWeight: "400",
                "&:hover": {
                  backgroundColor: "rgba(253, 201, 11, 0.94)",
                },
              }}
              style={{ height: "48px" }}
              onClick={handleBooking}
            >
              {/* <i
                className="fa-solid fa-calendar-check"
                style={{ color: "#09213c", marginRight: "8px" }}
              ></i> */}
              Book Now
            </Button>
          </div>
        </div>

        {/* Social Icons */}
        <div className="social-stack">
          <div className="animated bounce">
            <a
              href="https://www.facebook.com/WHSSportsHub"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={fb}
                alt="Facebook"
                loading="lazy"
                decoding="async"
                style={{ width: "44px", marginRight: "20px" }}
              />
            </a>
            <a
              href="https://instagram.com/whssportshub?igshid=YmMyMTA2M2Y="
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={insta}
                alt="Instagram"
                loading="lazy"
                decoding="async"
                style={{ width: "48px", marginRight: "20px" }}
              />
            </a>
            <a
              href="https://wa.me/919797733171?text=Hello%20!%0D%0ACan%20you%20help%20me%20?"
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "38px",
                height: "38px",
                backgroundColor: "#25D366",
                borderRadius: "50%",
                textDecoration: "none",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                verticalAlign: "middle",
                lineHeight: "1",
              }}
            >
              <i className="fab fa-whatsapp"></i>
            </a>
          </div>
        </div>

        {/* Footer Links */}
        <div className="links">
          <div onClick={() => navigate("/privacy-policy")}>Privacy Policy</div>
          <span>|</span>
          <div onClick={() => navigate("/terms-and-conditions")}>
            Terms & Conditions
          </div>
          <span>|</span>
          <div onClick={() => navigate("/refund-policy")}>
            Cancellation/Refund Policies
          </div>
        </div>
      </div>
    </>
  );
};

export default LocationLanding;
